import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CommentThread from '../components/CommentRow';
import { ArrowLeft, Pencil, Pin, Trash2, Eye, Clock, MessageSquare, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE = {
  edited: 'bg-amber-100 text-amber-800 border border-amber-200',
  pinned: 'bg-rose-100 text-rose-800 border border-rose-200',
  role: 'bg-rose-50 text-rose-700 border border-rose-100',
  default: 'bg-zinc-100 text-zinc-700 border border-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-700',
};

function Badge({ children, variant = 'default' }) {
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md inline-flex items-center gap-1 ${STYLE[variant]}`}>{children}</span>;
}

export default function WikiArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token, authHeaders } = useAuth();
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cText, setCText] = useState('');
  const [posting, setPosting] = useState(false);
  const [openReply, setOpenReply] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/wiki/${slug}`),
        axios.get(`${API}/wiki/${slug}/comments`),
      ]);
      setArticle(a.data); setComments(c.data || []);
    } catch { setArticle(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const onDelete = async () => {
    if (!window.confirm('Bu yazıyı silmek istediğinize emin misiniz?')) return;
    await axios.delete(`${API}/wiki/${slug}`, { headers: authHeaders });
    navigate('/wiki');
  };

  const addComment = async (text, parent_id = null) => {
    if (!text?.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(`${API}/wiki/${slug}/comments`, { body: text, parent_id }, { headers: authHeaders });
      setComments(v => [...v, r.data]);
      setCText('');
    } finally { setPosting(false); }
  };

  const delComment = async (id) => {
    if (!window.confirm('Yorumu silmek istiyor musunuz?')) return;
    await axios.delete(`${API}/wiki/${slug}/comments/${id}`, { headers: authHeaders });
    setComments(v => v.filter(c => c.id !== id && c.parent_id !== id));
  };

  const canDelete = (c) => user && (user.isAdmin || c.authorDiscordId === user.discord_id);

  const tops = comments.filter(c => !c.parent_id);
  const repliesOf = (id) => comments.filter(c => c.parent_id === id);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', color: 'var(--text)' }}>
      <Header />
      <main className="max-w-3xl mx-auto px-5 lg:px-8 pt-10 pb-24">
        <div className="flex items-center justify-between">
          <Link to="/wiki" className="pill-btn text-sm"><ArrowLeft size={14} /> Geri Dön</Link>
          {article && user?.canEdit && (
            <div className="flex gap-2">
              <Link to={`/wiki/${slug}/edit`} className="pill-btn text-sm"><Pencil size={14} /> Düzenle</Link>
              <button onClick={onDelete} className="pill-btn text-sm" style={{ color: '#f43f5e' }}><Trash2 size={14} /> Sil</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="mt-8 card-soft rounded-3xl h-96 animate-pulse" />
        ) : !article ? (
          <div className="mt-10 card-soft rounded-2xl p-10 text-center">
            <h2 className="text-2xl font-semibold">Yazı bulunamadı</h2>
          </div>
        ) : (
          <article className="mt-8 card-soft rounded-3xl overflow-hidden rise-in">
            {article.image && (
              <div className="aspect-[21/8] overflow-hidden" style={{ background: 'var(--pill-hover)' }}>
                <img src={article.image} alt={article.title} className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700" />
              </div>
            )}
            <div className="p-7 lg:p-10">
              <div className="flex items-center gap-2 flex-wrap">
                {article.edited && <Badge variant="edited"><Pencil size={10} /> Düzenlendi</Badge>}
                {article.pinned && <Badge variant="pinned"><Pin size={10} /> Sabit</Badge>}
                {article.category && <Badge>{article.category}</Badge>}
              </div>
              <h1 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight leading-[1.15]">{article.title}</h1>
              <div className="mt-5 flex items-center gap-4 text-sm flex-wrap pb-5 border-b" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2">
                  {article.authorAvatar && <img src={article.authorAvatar} alt="" className="w-6 h-6 rounded-full" />}
                  <span className="font-semibold">{article.author}</span>
                  {article.role && <Badge variant="role">{article.role}</Badge>}
                </div>
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><Clock size={12} /> {article.date}</span>
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><Eye size={12} /> {(article.views || 0).toLocaleString('tr-TR')} görüntülenme</span>
                <span className="inline-flex items-center gap-1" style={{ color: 'var(--text-muted)' }}><MessageSquare size={12} /> {comments.length} yorum</span>
              </div>
              <div className="mt-6">
                {article.excerpt && <p className="text-lg leading-relaxed font-medium">{article.excerpt}</p>}
                <div className="mt-5 whitespace-pre-wrap leading-[1.75] text-[15.5px]" style={{ color: 'var(--text-muted)' }}>
                  {article.body}
                </div>
              </div>
            </div>
          </article>
        )}

        <section className="mt-10 card-soft rounded-2xl p-7 lg:p-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MessageSquare size={18} /> Yorumlar
              <span className="text-sm font-normal" style={{ color: 'var(--text-muted)' }}>({comments.length})</span>
            </h2>
            <button onClick={load} className="pill-btn text-xs">Yenile</button>
          </div>
          {token ? (
            <form onSubmit={(e) => { e.preventDefault(); addComment(cText); }} className="mt-5 flex gap-2">
              {user?.avatar && <img src={user.avatar} alt="" className="w-9 h-9 rounded-full" />}
              <input value={cText} onChange={e => setCText(e.target.value)} placeholder="Yorumunuzu yazın..." className="to-input flex-1" />
              <button disabled={posting} className="pill-btn" style={{ background: 'var(--text)', color: 'var(--bg)', borderColor: 'transparent' }}>
                {posting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
                <span>Gönder</span>
              </button>
            </form>
          ) : (
            <p className="mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>Yorum yapmak için Discord ile giriş yapın.</p>
          )}
          <div className="mt-6 flex flex-col gap-5">
            {tops.map(c => (
              <CommentThread
                key={c.id}
                top={c}
                replies={repliesOf(c.id)}
                onReply={addComment}
                onDelete={delComment}
                canDelete={canDelete}
                openReply={openReply}
                setOpenReply={setOpenReply}
              />
            ))}
            {comments.length === 0 && (
              <p className="text-sm py-6 text-center" style={{ color: 'var(--text-muted)' }}>
                Henüz yorum yok. İlk yorumu sen yap!
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
